import {
  FULFILLMENT_FLAGGED_AS_RUSH,
  FULFILLMENT_ROLE_PREPARER,
  FULFILLMENT_ROLE_REVIEWER,
  FULFILLMENT_STATUS_CHANGED,
  FULFILLMENT_UNASSIGNED_AS_PREPARER,
  FULFILLMENT_UNASSIGNED_AS_REVIEWER,
  FULFILLMENT_WAITING_REMINDER,
  type FulfillmentRole,
  WORKBOARD_TASK_ROLE_MANAGER,
  type WorkboardTaskRole,
} from 'common/constants/notificationEventTypes';
import { NOTIFICATION_VARIANT, NotificationVariant } from './NotificationCenter.types';

/** User-visible copy for AI processing notifications (toast + panel card). */
export const NOTIFICATION_COPY = {
  title: {
    completed: 'AI Processing Complete',
    failed: 'AI Processing Failed',
    processing: 'Processing',
    started: 'Starting Up',
    default: 'AI Processing',
  },
  badge: {
    starting: 'Starting up…',
    processing: 'Processing…',
  },
  body: {
    startedSuffix: 'is queued for analysis',
    inProgressSuffix: 'is being analyzed',
    failedSuffix: 'processing failed',
    successSuffix: 'has been analyzed and categorized',
  },
  actions: {
    viewResults: 'View Results',
  },
  time: {
    justNow: 'Now',
  },
} as const;

export const NOTIFICATION_PROCESSING_TYPE_LABELS: Record<string, string> = {
  CAP_TABLE: 'Cap Table',
  CAP_TABLE_MAPPING: 'Cap Table Mapping',
  FINANCIAL_MAPPING: 'Financial Mapping',
  PDF_PROCESSING: 'PDF Processing',
  PARSING: 'Parsing',
  GENERATION: 'Generation',
};

export const RECENT_NOTIFICATION_THRESHOLD_MS = 2 * 60 * 1000;

export const NOTIFICATION_TOAST_DURATION_MS = 6000;

/** Shared icon / title colors keyed by visual state (toast + card). */
export const NOTIFICATION_VISUAL_THEME = {
  success: {
    iconColor: '#16a34a',
    titleColor: '#15803d',
  },
  error: {
    iconColor: '#dc2626',
    titleColor: '#dc2626',
  },
  active: {
    iconColor: '#7C3AED',
    titleColor: '#111',
  },
} as const;

export type NotificationVisual = keyof typeof NOTIFICATION_VISUAL_THEME;

export interface GlobalEventTheme {
  bg: string;
  border: string;
  iconBg: string;
  iconColor: string;
  titleColor: string;
}

const GLOBAL_EVENT_THEME_MAP: Record<string, GlobalEventTheme> = {
  feature_announcement: {
    bg: '#eef2ff',
    border: '#c7d2fe',
    iconBg: '#e0e7ff',
    iconColor: '#4f46e5',
    titleColor: '#4338ca',
  },
  system_maintenance: {
    bg: '#fffbeb',
    border: '#fcd34d',
    iconBg: '#fef3c7',
    iconColor: '#b45309',
    titleColor: '#92400e',
  },
  policy_update: {
    bg: '#f0fdfa',
    border: '#99f6e4',
    iconBg: '#ccfbf1',
    iconColor: '#0f766e',
    titleColor: '#115e59',
  },
};

const GLOBAL_EVENT_THEME_DEFAULT: GlobalEventTheme = {
  bg: '#eef2ff',
  border: '#c7d2fe',
  iconBg: '#e0e7ff',
  iconColor: '#4f46e5',
  titleColor: '#4338ca',
};

export function getGlobalEventTheme(eventType: string): GlobalEventTheme {
  return GLOBAL_EVENT_THEME_MAP[eventType] ?? GLOBAL_EVENT_THEME_DEFAULT;
}

// Neutral white/light-gray theme — no event-type variation
export const GENERAL_NOTIFICATION_THEME: GlobalEventTheme = {
  bg: '#fafafa',
  border: '#e2e8f0',
  iconBg: '#f1f5f9',
  iconColor: '#94a3b8',
  titleColor: '#475569',
};

const FULFILLMENT_THEME_MAP: Record<FulfillmentRole, GlobalEventTheme> = {
  [FULFILLMENT_ROLE_PREPARER]: {
    bg: '#eff6ff',
    border: '#bfdbfe',
    iconBg: '#dbeafe',
    iconColor: '#1d4ed8',
    titleColor: '#1e40af',
  },
  [FULFILLMENT_ROLE_REVIEWER]: {
    bg: '#f5f3ff',
    border: '#ddd6fe',
    iconBg: '#ede9fe',
    iconColor: '#6d28d9',
    titleColor: '#5b21b6',
  },
};

export function getFulfillmentTheme(role: FulfillmentRole): GlobalEventTheme {
  return FULFILLMENT_THEME_MAP[role];
}

export function getFulfillmentThemeForNotification(notification: { role?: FulfillmentRole }): GlobalEventTheme {
  return getFulfillmentTheme(notification.role ?? FULFILLMENT_ROLE_PREPARER);
}

export function getFulfillmentNotificationTitle(notification: {
  eventType: string;
  role?: FulfillmentRole;
  newModelStatusLabel?: string;
  previousModelStatusLabel?: string;
}): string {
  const { eventType, role, newModelStatusLabel, previousModelStatusLabel } = notification;

  if (eventType === FULFILLMENT_STATUS_CHANGED) {
    if (newModelStatusLabel && previousModelStatusLabel) {
      return `${previousModelStatusLabel} → ${newModelStatusLabel}`;
    }
    if (newModelStatusLabel) {
      return `Status changed to ${newModelStatusLabel}`;
    }
    return 'Status changed';
  }

  if (eventType === FULFILLMENT_WAITING_REMINDER) {
    return 'Still waiting';
  }

  if (eventType === FULFILLMENT_FLAGGED_AS_RUSH) {
    return 'Flagged as rush';
  }

  const roleLabel = role === FULFILLMENT_ROLE_REVIEWER ? 'Reviewer' : 'Preparer';
  if (eventType === FULFILLMENT_UNASSIGNED_AS_REVIEWER || eventType === FULFILLMENT_UNASSIGNED_AS_PREPARER) {
    return `Unassigned as ${roleLabel}`;
  }
  return `Assigned as ${roleLabel}`;
}

const WORKBOARD_TASK_THEME: GlobalEventTheme = {
  bg: '#e0f2f1',
  border: '#80cbc4',
  iconBg: '#b2dfdb',
  iconColor: '#00695c',
  titleColor: '#004d40',
};

export function getWorkboardTaskTheme(): GlobalEventTheme {
  return WORKBOARD_TASK_THEME;
}

export function getWorkboardTaskNotificationTitle(notification: { role?: WorkboardTaskRole }): string {
  const roleLabel = notification.role === WORKBOARD_TASK_ROLE_MANAGER ? 'Manager' : 'Assignee';
  return `Assigned as ${roleLabel}`;
}

export function getNotificationTitle(variant: NotificationVariant): string {
  if (variant === NOTIFICATION_VARIANT.GLOBAL) {
    return 'Announcement';
  }
  if (variant === NOTIFICATION_VARIANT.GENERAL) {
    return 'Notification';
  }
  if (variant === NOTIFICATION_VARIANT.COMPLETED) {
    return NOTIFICATION_COPY.title.completed;
  }
  if (variant === NOTIFICATION_VARIANT.FAILED) {
    return NOTIFICATION_COPY.title.failed;
  }
  if (variant === NOTIFICATION_VARIANT.PROCESSING) {
    return NOTIFICATION_COPY.title.processing;
  }
  if (variant === NOTIFICATION_VARIANT.STARTED) {
    return NOTIFICATION_COPY.title.started;
  }
  return NOTIFICATION_COPY.title.default;
}

export function getNotificationCompanyBodySuffix(variant: NotificationVariant): string {
  return variant === NOTIFICATION_VARIANT.FAILED
    ? NOTIFICATION_COPY.body.failedSuffix
    : NOTIFICATION_COPY.body.successSuffix;
}

export function getNotificationProcessingTypeLabel(processingType: string): string {
  return NOTIFICATION_PROCESSING_TYPE_LABELS[processingType] ?? processingType;
}
